import { z } from 'zod';
import { cursorPageSchema } from '../api/http.js';
import { isoDateTimeSchema, reasonSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
import { httpUrlSchema, prioritySchema, topicStatusSchema, topicVisibilitySchema, } from '../governance/topics.js';
import { taskStatusSchema } from '../tasks/tasks.js';
export const MEETING_STATUSES = [
    'REQUESTED',
    'SCHEDULED',
    'IN_PROGRESS',
    'COMPLETED',
    'CANCELLED',
];
export const meetingStatusSchema = z.enum(MEETING_STATUSES);
/** Meetings that can still change; completed and cancelled meetings are history. */
export const OPEN_MEETING_STATUSES = [
    'REQUESTED',
    'SCHEDULED',
    'IN_PROGRESS',
];
export function isOpenMeetingStatus(status) {
    return OPEN_MEETING_STATUSES.includes(status);
}
export const MEETING_PARTICIPANT_KINDS = ['REQUIRED', 'OPTIONAL'];
export const meetingParticipantKindSchema = z.enum(MEETING_PARTICIPANT_KINDS);
export const ATTENDANCE_STATUSES = ['UNKNOWN', 'ATTENDED', 'ABSENT', 'EXCUSED'];
export const attendanceStatusSchema = z.enum(ATTENDANCE_STATUSES);
export const AGENDA_ITEM_STATUSES = ['QUEUED', 'SCHEDULED', 'DISCUSSED', 'WITHDRAWN'];
export const agendaItemStatusSchema = z.enum(AGENDA_ITEM_STATUSES);
/** Spec §6.7: every agenda item exits with exactly one of these outcomes. */
export const AGENDA_OUTCOMES = [
    'DECISION',
    'TASK',
    'DEFERRED',
    'NO_ACTION',
    'FOLLOW_UP_TOPIC',
];
export const agendaOutcomeSchema = z.enum(AGENDA_OUTCOMES);
export const AGENDA_OUTCOME_LABELS = {
    DECISION: 'Decision',
    TASK: 'Task',
    DEFERRED: 'Deferred',
    NO_ACTION: 'No action',
    FOLLOW_UP_TOPIC: 'Follow-up topic',
};
/** Outcomes that must explain themselves in the note (why it waits, why nothing happens). */
export const AGENDA_OUTCOMES_REQUIRING_NOTE = [
    'DEFERRED',
    'NO_ACTION',
];
const memberRef = z.object({ memberId: uuidSchema, displayName: z.string() });
const numberedRef = z.object({ id: uuidSchema, number: z.number().int(), title: z.string() });
export const meetingParticipantSchema = memberRef.extend({
    kind: meetingParticipantKindSchema,
    attendance: attendanceStatusSchema,
});
export const meetingSummarySchema = z.object({
    id: uuidSchema,
    title: z.string(),
    status: meetingStatusSchema,
    scheduledStart: isoDateTimeSchema.nullable(),
    scheduledEnd: isoDateTimeSchema.nullable(),
    location: z.string().nullable(),
    organizer: memberRef,
    /** Agenda items that are not withdrawn. */
    agendaItemCount: z.number().int(),
    participantCount: z.number().int(),
    isParticipant: z.boolean(),
    /**
     * The agenda holds a restricted topic: the meeting is then visible to its organizer and
     * participants only (each of whom can see that topic).
     */
    isRestricted: z.boolean(),
    createdAt: isoDateTimeSchema,
});
export const MEETING_VIEWS = ['UPCOMING', 'PAST'];
export const meetingViewSchema = z.enum(MEETING_VIEWS);
export const meetingListQuerySchema = z.object({
    cursor: z.string().min(1).max(300).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    /** UPCOMING: requested/scheduled/in progress, soonest first. PAST: completed/cancelled, newest first. */
    view: meetingViewSchema.default('UPCOMING'),
});
export const meetingPageSchema = cursorPageSchema(meetingSummarySchema);
export const agendaTopicSchema = numberedRef.extend({
    status: topicStatusSchema,
    visibility: topicVisibilitySchema,
});
export const agendaItemSchema = z.object({
    id: uuidSchema,
    meetingId: uuidSchema.nullable(),
    rank: z.number().int().nullable(),
    status: agendaItemStatusSchema,
    /** Replaced by a neutral placeholder when the reader cannot see the linked topic. */
    title: z.string(),
    reason: z.string().nullable(),
    requestedDurationMinutes: z.number().int().nullable(),
    /** `null` when the item has no topic or the reader may not see it (see `topicHidden`). */
    topic: agendaTopicSchema.nullable(),
    topicHidden: z.boolean(),
    outcome: agendaOutcomeSchema.nullable(),
    outcomeNote: z.string().nullable(),
    outcomeDecision: numberedRef.nullable(),
    followUpTopic: numberedRef.nullable(),
    tasks: z.array(numberedRef.extend({ status: taskStatusSchema })),
    requestedBy: memberRef,
    createdAt: isoDateTimeSchema,
    version: z.number().int(),
});
export const meetingDetailSchema = meetingSummarySchema.extend({
    purpose: z.string().nullable(),
    conferenceUrl: z.string().nullable(),
    minutes: z.string().nullable(),
    minutesFinalizedAt: isoDateTimeSchema.nullable(),
    minutesFinalizedBy: memberRef.nullable(),
    cancelledAt: isoDateTimeSchema.nullable(),
    cancelReason: z.string().nullable(),
    participants: z.array(meetingParticipantSchema),
    agenda: z.array(agendaItemSchema),
    /** Sum of requested durations of active agenda items (minutes). */
    plannedMinutes: z.number().int(),
    /** Schedule, run, record outcomes and minutes (capability `meetings.organize`). */
    canOrganize: z.boolean(),
    updatedAt: isoDateTimeSchema,
    version: z.number().int(),
});
// Queue ------------------------------------------------------------------------------------------
export const QUEUE_SOURCES = ['TOPIC', 'REQUEST'];
export const queueSourceSchema = z.enum(QUEUE_SOURCES);
/**
 * One unresolved item waiting for a meeting: a topic marked Meeting Required, or an explicit request.
 * The server ranks the queue by priority, then deadline (passed first), then waiting time.
 */
export const queueEntrySchema = z.object({
    /** Stable reference used to add the entry to a meeting: `topic:<id>` or `request:<id>`. */
    key: z.string(),
    source: queueSourceSchema,
    title: z.string(),
    reason: z.string().nullable(),
    requestedDurationMinutes: z.number().int().nullable(),
    priority: prioritySchema,
    deadline: isoDateTimeSchema.nullable(),
    deadlinePassed: z.boolean(),
    waitingSince: isoDateTimeSchema,
    topic: agendaTopicSchema.nullable(),
    /** Explicit requests only. */
    requestId: uuidSchema.nullable(),
    requestVersion: z.number().int().nullable(),
    requestedBy: memberRef.nullable(),
    /** The reader may withdraw this request (requester or organizer, while it waits in the queue). */
    canWithdraw: z.boolean(),
    /** Already on the agenda of an open meeting. */
    plannedIn: z
        .object({
        meetingId: uuidSchema,
        title: z.string(),
        scheduledStart: isoDateTimeSchema.nullable(),
    })
        .nullable(),
    /** Human-readable ranking factors, e.g. "Critical priority", "Deadline passed". */
    rankReasons: z.array(z.string()),
});
export const meetingQueueSchema = z.object({ items: z.array(queueEntrySchema) });
export const queueKeySchema = z
    .string()
    .regex(/^(topic|request):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'Unknown queue entry');
export const agendaTitleSchema = z.string().trim().min(3).max(200);
const agendaReasonSchema = z.string().trim().max(2000).nullable().default(null);
const durationSchema = z.number().int().min(5).max(240).nullable().default(null);
/** Explicit queue request (spec §6.7: requested duration and reason). */
export const requestAgendaItemRequestSchema = z.object({
    title: agendaTitleSchema,
    reason: agendaReasonSchema,
    requestedDurationMinutes: durationSchema,
    /** Optional topic the request is about; it must be visible to the requester. */
    topicId: uuidSchema.nullable().default(null),
});
export const withdrawAgendaRequestSchema = z.object({ version: versionSchema });
// Meetings ---------------------------------------------------------------------------------------
export const meetingTitleSchema = z.string().trim().min(3).max(200);
const participantInputSchema = z.object({
    memberId: uuidSchema,
    kind: meetingParticipantKindSchema.default('REQUIRED'),
});
function uniqueParticipants(participants) {
    return new Set(participants.map((p) => p.memberId)).size === participants.length;
}
function scheduleIsConsistent(value) {
    const start = value.scheduledStart ?? null;
    const end = value.scheduledEnd ?? null;
    if (start === null || end === null)
        return end === null;
    return Date.parse(end) > Date.parse(start);
}
const meetingFields = {
    title: meetingTitleSchema,
    purpose: z.string().trim().max(5000).nullable().default(null),
    location: z.string().trim().max(300).nullable().default(null),
    conferenceUrl: httpUrlSchema.nullable().default(null),
    participants: z.array(participantInputSchema).min(1).max(50),
};
export const createMeetingRequestSchema = z
    .object({
    ...meetingFields,
    /** Without a start the meeting stays REQUESTED until someone schedules it. */
    scheduledStart: isoDateTimeSchema.nullable().default(null),
    scheduledEnd: isoDateTimeSchema.nullable().default(null),
    /** Queue entries to put on the agenda right away, in this order. */
    agendaKeys: z.array(queueKeySchema).max(30).default([]),
})
    .refine((value) => uniqueParticipants(value.participants), {
    message: 'Each participant can be added once',
    path: ['participants'],
})
    .refine(scheduleIsConsistent, {
    message: 'The end must be after the start (and needs a start)',
    path: ['scheduledEnd'],
});
export const updateMeetingRequestSchema = z
    .object({ ...meetingFields, version: versionSchema })
    .refine((value) => uniqueParticipants(value.participants), {
    message: 'Each participant can be added once',
    path: ['participants'],
});
export const scheduleMeetingRequestSchema = z
    .object({
    version: versionSchema,
    scheduledStart: isoDateTimeSchema,
    scheduledEnd: isoDateTimeSchema.nullable().default(null),
})
    .refine(scheduleIsConsistent, {
    message: 'The end must be after the start',
    path: ['scheduledEnd'],
});
export const meetingVersionRequestSchema = z.object({ version: versionSchema });
export const cancelMeetingRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
export const setAttendanceRequestSchema = z.object({
    version: versionSchema,
    attendance: z
        .array(z.object({ memberId: uuidSchema, attendance: attendanceStatusSchema }))
        .min(1)
        .max(50),
});
export const saveMinutesRequestSchema = z.object({
    version: versionSchema,
    minutes: z.string().trim().max(50_000),
});
// Agenda -----------------------------------------------------------------------------------------
export const addAgendaItemsRequestSchema = z
    .object({
    version: versionSchema,
    /** Queue entries to append, in order. */
    keys: z.array(queueKeySchema).max(30).default([]),
    /** Ad-hoc item that did not go through the queue. */
    custom: z
        .object({
        title: agendaTitleSchema,
        reason: agendaReasonSchema,
        requestedDurationMinutes: durationSchema,
    })
        .nullable()
        .default(null),
})
    .refine((value) => value.keys.length > 0 || value.custom !== null, {
    message: 'Choose at least one item to add',
    path: ['keys'],
})
    .refine((value) => new Set(value.keys).size === value.keys.length, {
    message: 'The same item is listed twice',
    path: ['keys'],
});
/** Fills the agenda from the ranked queue until the time budget is used. */
export const generateAgendaRequestSchema = z.object({
    version: versionSchema,
    budgetMinutes: z.number().int().min(15).max(480).default(60),
    /** Assumed length of entries that did not request a duration. */
    defaultItemMinutes: z.number().int().min(5).max(120).default(15),
});
export const reorderAgendaRequestSchema = z
    .object({ version: versionSchema, itemIds: z.array(uuidSchema).min(1).max(100) })
    .refine((value) => new Set(value.itemIds).size === value.itemIds.length, {
    message: 'Each item can appear once',
    path: ['itemIds'],
});
export const removeAgendaItemRequestSchema = z.object({ version: versionSchema });
export const recordAgendaOutcomeRequestSchema = z
    .object({
    /** Agenda item version. */
    version: versionSchema,
    outcome: agendaOutcomeSchema,
    note: z.string().trim().max(5000).nullable().default(null),
    decisionId: uuidSchema.nullable().default(null),
    followUpTopicId: uuidSchema.nullable().default(null),
})
    .refine((value) => value.outcome !== 'DECISION' || value.decisionId !== null, {
    message: 'Choose the decision that was taken',
    path: ['decisionId'],
})
    .refine((value) => value.outcome !== 'FOLLOW_UP_TOPIC' || value.followUpTopicId !== null, {
    message: 'Choose the follow-up topic',
    path: ['followUpTopicId'],
})
    .refine((value) => !AGENDA_OUTCOMES_REQUIRING_NOTE.includes(value.outcome) ||
    (value.note !== null && value.note.length >= 3), { message: 'Explain this outcome in a short note', path: ['note'] });
//# sourceMappingURL=meetings.js.map